import { useAxiosConfig } from '@shared/api/AxiosConfigProvider';
import { refreshQueries } from '@shared/api/hooks/refresh-queries';
import {
  getCompanyReportsActivitiesHeatListQueryKey,
  getCompanyReportsRetrieveQueryKey,
  useCompanyReportsActivitiesDestroy,
  useCompanyReportsActivitiesHeatCreate,
  useCompanyReportsActivitiesHeatList,
  useCompanyReportsActivitiesHeatPreviewRetrieve,
  useCompanyReportsActivitiesHeatRetrieve,
  useCompanyReportsActivitiesHeatUpdate,
} from '@shared/api/lib/company-reports/company-reports';
import {
  CompanyReportsActivitiesHeatListParams,
  CompanyReportsActivitiesHeatPreviewRetrieveParams,
  HeatActivitiesList,
  HeatActivityRetrieve,
  PaginatedHeatActivitiesListList,
} from '@shared/api/types';
import { useQueryClient } from '@tanstack/react-query';

export const useGetHeatActivityList = (
  companyReportUuid: string,
  params?: CompanyReportsActivitiesHeatListParams
) => {
  return useCompanyReportsActivitiesHeatList<PaginatedHeatActivitiesListList>(
    companyReportUuid,
    params,
    {
      request: useAxiosConfig(),
      query: {
        keepPreviousData: true,
        cacheTime: 1000 * 60 * 60 * 24,
      },
    }
  );
};

export const useCreateHeatActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsActivitiesHeatCreate<HeatActivitiesList>({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: (data, variables) => {
        void refreshQueries(
          queryClient,
          getCompanyReportsRetrieveQueryKey(variables.companyReportUuid)
        );
        return refreshQueries(
          queryClient,
          getCompanyReportsActivitiesHeatListQueryKey(
            variables.companyReportUuid
          )
        );
      },
    },
  });
};

export const useGetHeatActivity = ({
  heatingActivityUuid,
  reportUuid,
}: {
  heatingActivityUuid?: string;
  reportUuid: string;
}) => {
  return useCompanyReportsActivitiesHeatRetrieve<HeatActivityRetrieve>(
    reportUuid,
    heatingActivityUuid!,
    {
      request: useAxiosConfig(),
      query: {
        enabled: !!heatingActivityUuid,
      },
    }
  );
};

export const useUpdateHeatActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsActivitiesHeatUpdate<HeatActivitiesList>({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: (data, variables) => {
        void refreshQueries(
          queryClient,
          getCompanyReportsRetrieveQueryKey(variables.companyReportUuid)
        );
        return refreshQueries(
          queryClient,
          getCompanyReportsActivitiesHeatListQueryKey(
            variables.companyReportUuid
          )
        );
      },
    },
  });
};

export const useDeleteHeatActivity = () => {
  const queryClient = useQueryClient();
  return useCompanyReportsActivitiesDestroy({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: (data, variables) => {
        void refreshQueries(
          queryClient,
          getCompanyReportsRetrieveQueryKey(variables.companyReportUuid)
        );
        return refreshQueries(
          queryClient,
          getCompanyReportsActivitiesHeatListQueryKey(
            variables.companyReportUuid
          )
        );
      },
    },
  });
};

export const useGetHeatPreview = ({
  companyReportUuid,
  params,
  enabled,
}: {
  companyReportUuid: string;
  params?: CompanyReportsActivitiesHeatPreviewRetrieveParams;
  enabled: boolean;
}) => {
  return useCompanyReportsActivitiesHeatPreviewRetrieve(
    companyReportUuid,
    params,
    {
      request: useAxiosConfig(),
      query: {
        enabled,
        cacheTime: 0,
        staleTime: 0,
      },
    }
  );
};
